/**
 * SAID-DIIS — Shared Edit Profile modal.
 *
 * Opens from the user dropdown in every portal (Student, Clerk, Admin,
 * SuperAdmin). Reuses the existing `PUT /api/auth/me` endpoint — which
 * now accepts both `full_name` and `email` — and the existing Change
 * Password modal (no password editing happens here).
 *
 * Field order is fixed by product spec:
 *   PROFILE PICTURE → NAME → EMAIL ADDRESS → PASSWORD (button only)
 *
 * Avatar upload/remove uses `POST/DELETE /api/auth/me/avatar` (own-user only).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { KeyRound, X, Upload, Trash2 } from 'lucide-react';
import Avatar from './Avatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AVATAR_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_MAX = 2 * 1024 * 1024;

export default function EditProfileModal({
  open,
  onClose,
  user,
  onRefreshUser,
  onOpenChangePassword,
  onToast,
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState('');
  const [avatarMsg, setAvatarMsg] = useState('');
  const nameRef = useRef(null);
  const fileRef = useRef(null);

  // Hydrate from the authenticated user every time the modal opens so
  // stale values from a previous session can't leak in.
  useEffect(() => {
    if (open) {
      setFullName(user?.full_name || '');
      setEmail(user?.email || '');
      setErr('');
      setAvatarMsg('');
      // Autofocus Name on open
      window.setTimeout(() => nameRef.current && nameRef.current.focus(), 50);
    }
  }, [open, user?.full_name, user?.email]);

  if (!open) return null;

  const dirty =
    (fullName.trim() !== (user?.full_name || '').trim()) ||
    (email.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase());

  const handleAvatarPick = () => {
    setAvatarMsg('');
    if (fileRef.current) fileRef.current.click();
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so picking the same file twice still fires onChange.
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    if (!AVATAR_MIME.includes(file.type)) {
      setAvatarMsg('Use a JPG, PNG or WebP image.');
      return;
    }
    if (file.size > AVATAR_MAX) {
      setAvatarMsg('Image must be 2 MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/auth/me/avatar`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onRefreshUser) await onRefreshUser();
      onToast && onToast('Profile picture updated.', 'ok');
    } catch (ex) {
      setAvatarMsg(ex?.response?.data?.detail || 'Could not upload picture.');
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!user?.avatar_url) return;
    setRemoving(true);
    setAvatarMsg('');
    try {
      await axios.delete(`${API}/auth/me/avatar`);
      if (onRefreshUser) await onRefreshUser();
      onToast && onToast('Profile picture removed.', 'ok');
    } catch (ex) {
      setAvatarMsg(ex?.response?.data?.detail || 'Could not remove picture.');
    } finally {
      setRemoving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setErr('');
    const name = fullName.trim();
    const mail = email.trim();
    if (!name) {
      setErr('Name cannot be blank.');
      return;
    }
    if (!EMAIL_RE.test(mail)) {
      setErr('Enter a valid email address.');
      return;
    }
    if (!dirty) {
      onClose && onClose();
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me`, { full_name: name, email: mail });
      if (onRefreshUser) await onRefreshUser();
      onToast && onToast('Profile updated.', 'ok');
      onClose && onClose();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Could not update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      data-testid="edit-profile-modal"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose && onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-profile-title"
    >
      <form
        onSubmit={handleSave}
        className="bg-white rounded-lg w-full max-w-md shadow-xl"
        style={{ border: '1px solid var(--diis-hairline-strong)' }}
      >
        <div
          className="flex items-center justify-between"
          style={{ padding: '18px 22px', borderBottom: '1px solid var(--diis-hairline)' }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--diis-ink-subtle)',
                fontWeight: 500,
              }}
            >
              Profile
            </div>
            <h3
              id="edit-profile-title"
              style={{
                margin: '2px 0 0',
                fontFamily: 'var(--diis-font-display)',
                fontSize: 20,
                fontWeight: 500,
                color: 'var(--diis-ink)',
                letterSpacing: '-0.01em',
              }}
            >
              Edit profile
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="edit-profile-close-btn"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--diis-ink-subtle)',
              padding: 4,
              display: 'inline-flex',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {err && (
            <div
              role="alert"
              data-testid="edit-profile-error"
              style={{
                padding: '10px 12px',
                border: '1px solid var(--diis-danger)',
                background: 'var(--diis-danger-soft)',
                color: 'var(--diis-danger)',
                borderRadius: 'var(--diis-radius)',
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {err}
            </div>
          )}

          {/* 0. PROFILE PICTURE */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--diis-ink-muted)',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Profile picture
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Avatar user={user} size={72} testId="edit-profile-avatar-preview" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleAvatarPick}
                    disabled={uploading || removing}
                    data-testid="edit-profile-avatar-upload-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '7px 12px', fontSize: 12, fontWeight: 500,
                      color: 'var(--diis-accent-ink)', background: 'transparent',
                      border: '1px solid var(--diis-accent)',
                      borderRadius: 'var(--diis-radius)',
                      cursor: uploading ? 'wait' : 'pointer',
                    }}
                  >
                    <Upload size={13} />
                    {user?.avatar_url ? (uploading ? 'Uploading…' : 'Change') : (uploading ? 'Uploading…' : 'Upload')}
                  </button>
                  {user?.avatar_url && (
                    <button
                      type="button"
                      onClick={handleAvatarRemove}
                      disabled={removing || uploading}
                      data-testid="edit-profile-avatar-remove-btn"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 12px', fontSize: 12, fontWeight: 500,
                        color: 'var(--diis-ink-muted)', background: 'transparent',
                        border: '1px solid var(--diis-hairline-strong)',
                        borderRadius: 'var(--diis-radius)',
                        cursor: removing ? 'wait' : 'pointer',
                      }}
                    >
                      <Trash2 size={13} />
                      {removing ? 'Removing…' : 'Remove'}
                    </button>
                  )}
                </div>
                <span style={{ fontSize: 11, color: 'var(--diis-ink-subtle)' }}>
                  JPG, PNG or WebP · max 2 MB
                </span>
                {avatarMsg && (
                  <span
                    data-testid="edit-profile-avatar-msg"
                    style={{
                      fontSize: 12,
                      color: avatarMsg.startsWith('Could not') || avatarMsg.includes('MB') || avatarMsg.includes('Use a') ? 'var(--diis-danger)' : 'var(--diis-ink-muted)',
                    }}
                  >
                    {avatarMsg}
                  </span>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarFile}
                  data-testid="edit-profile-avatar-file-input"
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* 1. NAME */}
          <div>
            <label
              htmlFor="edit-profile-name"
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--diis-ink-muted)',
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              Name
            </label>
            <input
              id="edit-profile-name"
              ref={nameRef}
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Dr. John Smith"
              data-testid="edit-profile-name-input"
              style={{
                width: '100%',
                padding: '11px 12px',
                fontSize: 15,
                border: '1px solid var(--diis-hairline-strong)',
                borderRadius: 'var(--diis-radius)',
                color: 'var(--diis-ink)',
                background: 'var(--diis-surface)',
                outline: 'none',
                fontFamily: 'var(--diis-font-sans)',
              }}
            />
          </div>

          {/* 2. EMAIL ADDRESS */}
          <div>
            <label
              htmlFor="edit-profile-email"
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--diis-ink-muted)',
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              Email address
            </label>
            <input
              id="edit-profile-email"
              type="text"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourdomain.com"
              data-testid="edit-profile-email-input"
              style={{
                width: '100%',
                padding: '11px 12px',
                fontSize: 15,
                border: '1px solid var(--diis-hairline-strong)',
                borderRadius: 'var(--diis-radius)',
                color: 'var(--diis-ink)',
                background: 'var(--diis-surface)',
                outline: 'none',
                fontFamily: 'var(--diis-font-sans)',
              }}
            />
          </div>

          {/* 3. PASSWORD */}
          <div
            style={{
              paddingTop: 14,
              borderTop: '1px solid var(--diis-hairline)',
            }}
          >
            <label
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--diis-ink-muted)',
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              Password
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span style={{ fontSize: 13, color: 'var(--diis-ink-muted)' }}>
                Managed in a dedicated secure flow.
              </span>
              <button
                type="button"
                onClick={() => { onClose && onClose(); onOpenChangePassword && onOpenChangePassword(); }}
                data-testid="edit-profile-change-password-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--diis-accent-ink)',
                  background: 'transparent',
                  border: '1px solid var(--diis-accent)',
                  borderRadius: 'var(--diis-radius)',
                  cursor: 'pointer',
                }}
              >
                <KeyRound size={14} />
                Change password
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 22px',
            borderTop: '1px solid var(--diis-hairline)',
            background: 'var(--diis-canvas, #faf8f3)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-profile-cancel-btn"
            style={{
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--diis-ink-muted)',
              background: 'transparent',
              border: '1px solid var(--diis-hairline-strong)',
              borderRadius: 'var(--diis-radius)',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-profile-save-btn"
            style={{
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: 600,
              color: 'white',
              background: 'var(--diis-accent)',
              border: '1px solid var(--diis-accent)',
              borderRadius: 'var(--diis-radius)',
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.75 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
